// Broadcast popup — admin "broadcast" messages the user has not acknowledged
// yet open as a large dialog that only closes via "OK, I have read it" (no
// outside-click, no Esc, no X). Mounted once in each shell (Mobile /
// Distributor / Office). It asks the server on mount, when the app comes back
// to the foreground, and when the bell's own poll spots a new message — never
// on a timer of its own (battery). Backend: routes_broadcasts.py
// (/broadcasts/pending, /broadcasts/{id}/ack).
//
// Also exports the bits pages/Messages.jsx reuses (attachment list, linkified
// body, date formatting) so both screens open files the same way.
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { toast } from "sonner";
import {
  AlertTriangle, Megaphone, Paperclip, FileText, FileSpreadsheet, Image as ImageIcon,
  Loader2, Check, Download, Eye,
} from "lucide-react";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

// window events shared with NotificationBell / Messages / MoreMenu:
// CHECK   — "a new broadcast notification arrived, look for pending popups"
// CHANGED — "a broadcast was just acknowledged, refresh counts / lists"
export const BROADCAST_CHECK_EVENT = "tulsi:broadcast-check";
export const BROADCASTS_CHANGED_EVENT = "tulsi:broadcasts-changed";

export function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

// Backend stores YYYY-MM-DD; users read DD-MM-YYYY.
export function fmtYmd(ymd) {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-");
  return d && m && y ? `${d}-${m}-${y}` : ymd;
}

function fmtSize(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function isViewable(att) {
  const ct = att?.content_type || "";
  return ct.startsWith("image/") || ct === "application/pdf" || /\.(pdf|jpe?g|png|webp)$/i.test(att?.name || "");
}

function fileIcon(att) {
  const name = (att?.name || "").toLowerCase();
  if ((att?.content_type || "").startsWith("image/") || /\.(jpe?g|png|webp)$/.test(name)) return ImageIcon;
  if (/\.xlsx?$/.test(name)) return FileSpreadsheet;
  return FileText;
}

// A plain <a href>/<img src> to the API carries no token, so files are fetched
// with the bearer header and handed to the browser as a blob URL.
async function fetchAttachment(att, inline) {
  const res = await fetch(`${API_BASE}/broadcasts/attachments/${encodeURIComponent(att.id)}?inline=${inline ? 1 : 0}`,
    { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// PDFs and photos open in a new tab, other files download. The tab is opened
// before the fetch so pop-up blockers count it as part of the tap; if the
// browser blocks it anyway, the file is downloaded instead.
async function openAttachment(att) {
  const viewable = isViewable(att);
  const win = viewable ? window.open("", "_blank") : null;
  try {
    const blob = await fetchAttachment(att, viewable);
    if (win) {
      const url = URL.createObjectURL(blob);
      win.location.href = url;
      // free the file's memory (up to 10 MB) once the new tab has loaded it
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else saveBlob(blob, att.name);
  } catch {
    if (win) win.close();
    toast.error("Could not open this file. Please check your internet and try again.");
  }
}

export function BroadcastAttachments({ attachments, testid = "bc-attachments" }) {
  const [busy, setBusy] = useState(null);
  if (!attachments?.length) return null;
  const open = async (att) => {
    setBusy(att.id);
    try { await openAttachment(att); } finally { setBusy(null); }
  };
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-neutral-400 flex items-center gap-1">
        <Paperclip className="h-3 w-3" /> Attachments ({attachments.length})
      </div>
      {attachments.map((att) => {
        const Icon = fileIcon(att);
        const viewable = isViewable(att);
        return (
          <button
            key={att.id}
            type="button"
            onClick={() => open(att)}
            disabled={busy === att.id}
            data-testid={`${testid}-${att.id}`}
            className="w-full flex items-center gap-2.5 rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-left hover:border-[#6D2C91]/40 hover:bg-[#6D2C91]/5 active:bg-[#6D2C91]/10 transition-colors disabled:opacity-60"
          >
            <div className="h-8 w-8 rounded-lg bg-[#6D2C91]/10 text-[#6D2C91] flex items-center justify-center shrink-0">
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-neutral-900 truncate">{att.name}</div>
              <div className="text-[11px] text-neutral-500">
                {fmtSize(att.size)} · {viewable ? "Tap to open" : "Tap to download"}
              </div>
            </div>
            {busy === att.id
              ? <Loader2 className="h-4 w-4 animate-spin text-neutral-400 shrink-0" />
              : viewable
                ? <Eye className="h-4 w-4 text-[#6D2C91] shrink-0" />
                : <Download className="h-4 w-4 text-[#6D2C91] shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}

// Message text with web links made tappable; everything else stays plain text
// (whitespace/new lines kept by the caller's `whitespace-pre-wrap`).
// (a trailing full stop / comma / bracket stays text, not part of the link)
const URL_RE = /(https?:\/\/[^\s<>"']*[^\s<>"'.,;:!?)\]])/g;
export function LinkifiedText({ text }) {
  const parts = String(text || "").split(URL_RE);
  return parts.map((part, i) => (i % 2 === 1 ? (
    <a key={i} href={part} target="_blank" rel="noopener noreferrer"
       className="text-[#6D2C91] underline break-all">{part}</a>
  ) : <Fragment key={i}>{part}</Fragment>));
}

export default function BroadcastPopup() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [total, setTotal] = useState(0); // all still waiting; the server sends at most 10 at a time
  const [pos, setPos] = useState(0);
  const [acking, setAcking] = useState(false);
  const inFlight = useRef(false);
  const ackingRef = useRef(false);
  const userId = user?.id;
  const mustChange = !!user?.must_change_password;

  // true when a fresh queue was loaded
  const load = useCallback(async () => {
    const { data } = await api.get("/broadcasts/pending", { skipAuthRedirect: true });
    const items = Array.isArray(data?.items) ? data.items : [];
    setQueue(items);
    setTotal(Math.max(items.length, Number(data?.total) || 0));
    setPos(0);
    return true;
  }, []);

  const check = useCallback(async () => {
    if (!userId || mustChange || !getToken()) return;
    // don't reshuffle the queue under the user's thumb while an OK is saving
    if (inFlight.current || ackingRef.current) return;
    inFlight.current = true;
    try {
      await load();
    } catch (_e) {
      /* silent — it is asked again the next time the app is opened */
    } finally {
      inFlight.current = false;
    }
  }, [userId, mustChange, load]);

  useEffect(() => {
    if (!userId) { setQueue([]); setTotal(0); setPos(0); return undefined; }
    check();
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener(BROADCAST_CHECK_EVENT, check);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener(BROADCAST_CHECK_EVENT, check);
    };
  }, [userId, check]);

  const current = userId ? queue[pos] || null : null;

  const ack = async () => {
    if (!current || ackingRef.current) return;
    ackingRef.current = true;
    setAcking(true);
    let refilled = false;
    try {
      await api.post(`/broadcasts/${encodeURIComponent(current.id)}/ack`);
      window.dispatchEvent(new Event(BROADCASTS_CHANGED_EVENT));
      // Last of this batch but more are waiting on the server: fetch the next
      // batch now instead of closing until the app is opened again.
      if (pos + 1 >= queue.length && total > queue.length) {
        refilled = await load().catch(() => false);
      }
    } catch (e) {
      // Never trap the user behind the popup: move on anyway. The message is
      // still unread on the server, so it comes back next time the app opens.
      toast.error(e?.response
        ? formatApiError(e)
        : "Could not reach the server. This message will show again later.");
    } finally {
      ackingRef.current = false;
      setAcking(false);
      if (!refilled) setPos((p) => p + 1);
    }
  };

  // Keep the Root mounted and let Radix close it normally after the last OK
  // (its own cleanup restores page scrolling / clicks); `shown` keeps the
  // last message's text on screen for that final closing render.
  const lastShown = useRef(null);
  if (current) lastShown.current = current;
  const shown = current || lastShown.current;
  if (!shown) return null;
  const urgent = shown.priority === "urgent";

  return (
    <DialogPrimitive.Root open={!!current} onOpenChange={() => { /* closes only via the OK button */ }}>
      <DialogPrimitive.Portal>
        {/* Content sits inside the overlay (flex-centred) instead of being
            translate-centred, so the open animation can't knock it off-centre
            and it always keeps a 12px gutter on a phone. */}
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-3 data-[state=open]:animate-in data-[state=open]:fade-in-0"
        >
          <DialogPrimitive.Content
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            onOpenAutoFocus={(e) => e.preventDefault()}
            className="relative w-full max-w-md max-h-[88vh] flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
            data-testid="broadcast-popup"
          >
            <div className={`shrink-0 px-5 pt-4 pb-3.5 text-white ${urgent ? "bg-red-600" : "tulsi-gradient"}`}>
              <div className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-widest text-white/90">
                <span className="flex items-center gap-1.5 min-w-0">
                  {urgent ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> : <Megaphone className="h-3.5 w-3.5 shrink-0" />}
                  <span className="truncate">{urgent ? "Urgent message" : "Message"}</span>
                </span>
                {total > 1 && (
                  <span className="shrink-0 rounded-full bg-white/20 px-2 py-0.5 normal-case tracking-normal"
                        data-testid="broadcast-popup-counter">
                    {Math.min(pos + 1, total)} of {total}
                  </span>
                )}
              </div>
              <DialogPrimitive.Title className="mt-1.5 text-lg font-extrabold leading-snug break-words"
                                     data-testid="broadcast-popup-title">
                {shown.title}
              </DialogPrimitive.Title>
              <div className="mt-1 text-xs text-white/85 break-words">
                From {shown.created_by_name || "Admin"}
                {shown.created_at ? ` · ${fmtWhen(shown.created_at)}` : ""}
              </div>
            </div>

            {/* keyed so each next message starts scrolled to the top */}
            <div key={shown.id} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
              <DialogPrimitive.Description asChild>
                <div className="text-[15px] leading-relaxed text-neutral-800 whitespace-pre-wrap break-words"
                     data-testid="broadcast-popup-body">
                  <LinkifiedText text={shown.body} />
                </div>
              </DialogPrimitive.Description>
              <BroadcastAttachments attachments={shown.attachments} testid="broadcast-popup-att" />
            </div>

            <div className="shrink-0 border-t border-neutral-100 px-5 py-3">
              <Button
                type="button"
                onClick={ack}
                disabled={acking}
                data-testid="broadcast-popup-ack"
                className={`w-full h-12 rounded-xl text-base font-semibold text-white ${urgent ? "bg-red-600 hover:bg-red-700" : "tulsi-gradient"}`}
              >
                {acking ? <Loader2 className="animate-spin" /> : <Check />}
                OK, I have read it
              </Button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
